import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageFrame } from '../../components/PageFrame';
import { SafeExitLink } from './SafeExitLink';
import type { createLearnerSessionClient } from '../../platform/learner/learner-session-client';

// Same calm, no-technical-detail discipline as HelpState/PracticeTask.
const NOT_SAVED_NOTE = 'This might not have been saved — you can try again.';

type Props = {
  client: Pick<ReturnType<typeof createLearnerSessionClient>, 'sendAction'>;
};

// S-SCR-005 Pause and Resume: a pure state-transition screen. Pause was
// already confirmed successful by the server before PracticeTask navigated
// here, so there is nothing to record on mount and no reason to call
// getCurrentPractice() — no item content or prompt is fetched or rendered
// on this screen at all, per the controlling prompt's explicit "no content
// visible" requirement for the paused state.
export function PausedState({ client }: Props) {
  const navigate = useNavigate();
  const [resuming, setResuming] = useState(false);
  const [resumeFailed, setResumeFailed] = useState(false);

  async function handleResume() {
    setResuming(true);
    setResumeFailed(false);
    try {
      await client.sendAction('resume', {}, crypto.randomUUID());
      // Only navigate on confirmed success — never claim resume happened
      // via a `finally` or before the server has actually confirmed it.
      void navigate('/learn/practice', { replace: true });
    } catch {
      setResuming(false);
      setResumeFailed(true);
    }
  }

  return (
    <PageFrame title="Paused" audience="learner">
      <p className="mt-4 text-lg leading-relaxed text-ink-soft">
        Take a break. Your questions are hidden while you're paused.
      </p>
      <p className="mt-2 text-lg leading-relaxed text-ink-soft">
        Press Resume when you're ready to carry on.
      </p>

      {resumeFailed && (
        <p role="alert" className="mt-4 text-sm text-muted">
          {NOT_SAVED_NOTE}
        </p>
      )}

      <div className="mt-6">
        <button
          type="button"
          disabled={resuming}
          className="dm-btn-primary w-full"
          onClick={() => {
            void handleResume();
          }}
        >
          Resume
        </button>
      </div>

      <SafeExitLink />
    </PageFrame>
  );
}
